import { CreateDialogFieldCompanion } from 'components/create-dialog/CreateDialogRequired';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { PartyType } from 'types/contacts';
import PermissionButton from 'components/buttons/PermissionButton';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal.tsx';
import InfoTooltip from 'components/InfoTooltip';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import { MatterFormState } from './useMatterForm';

/** Opponents, targets and other parties. */
export const OpponentsFields = ({ form }: { form: MatterFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { clientParty, isNoOpponent, openAddNewContactModal, openContactTypePicker } = form;

    return (
        <>
            {/* Unticked, it makes Opponents required, which the create dialog then draws among its required
                fields: the checkbox goes there with it, so the requirement can be lifted where it shows. */}
            <CreateDialogFieldCompanion field="opponentParties">
                <CheckboxInput
                    onChange={(isNoOpponent) => {
                        if (isNoOpponent) {
                            setValue('opponentParties', []);
                        }
                    }}
                    control={control}
                    name="isNoOpponent"
                    label="matters.no-opponent"
                />
            </CreateDialogFieldCompanion>
            {!isNoOpponent ? (
                <ContactDynamicMultiSelectPicker
                    setValue={setValue}
                    control={control}
                    label="matters.opponents"
                    placeholder="matters.select-opponents"
                    name="opponentParties"
                    paginated={{
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        filters: clientParty?.id ? `id!=${clientParty.id}` : undefined,
                    }}
                    required
                    noOptionsContent={
                        <PermissionButton
                            permissions={[
                                'CREATE.PARTIES',
                                'CREATE.PARTIES.CLIENTS',
                                'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                            ]}
                            variant="contained"
                            color="secondary"
                            size="extraSmall"
                            onClick={() => openContactTypePicker('opponentParties')}
                        >
                            <FormattedMessage id="contacts.add-new-contact" />
                        </PermissionButton>
                    }
                    PickerModalComponent={ContactPickerModal}
                />
            ) : null}
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="matters.targets"
                placeholder="matters.select-targets"
                name="targetParties"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                }}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openContactTypePicker('targetParties')}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
                PickerModalComponent={ContactPickerModal}
            />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="matters.other-parties"
                placeholder="matters.select-other-parties"
                name="otherParties"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                }}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openContactTypePicker('otherParties')}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
                rightLabelComponent={<InfoTooltip text={'matters.tooltips.other-contacts'} />}
                PickerModalComponent={ContactPickerModal}
            />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="matters.judges"
                placeholder="matters.select-judges"
                name="judgeParties"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                    filters: `partyType==${PartyType.PERSON}`,
                }}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={() => openAddNewContactModal('judgeParties')}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
                rightLabelComponent={<InfoTooltip text={'matters.tooltips.other-contacts'} />}
                PickerModalComponent={ContactPickerModal}
            />
        </>
    );
};
